import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Table from '../components/table/Table';
import StatusCard from '../components/status-card/StatusCard';
import { checkInsolvencyByIco, fetchCustomers, fetchUnpaidInvoices } from '../lib/crmClient';

function firstValue(source, names) {
  for (const name of names) {
    const value = source && source[name];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return '';
}

function asDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('cs-CZ');
}

function asDateTime(value) {
  if (!value) return '-';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('cs-CZ');
}

function asAmount(value, currency) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: String(currency || 'CZK').toUpperCase(),
    maximumFractionDigits: 2,
  }).format(number);
}

function overdueDays(value) {
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86400000));
}

function normalizeIco(value) {
  return String(value || '').replace(/\D/g, '');
}

function lastOverdueReminder(log) {
  const reminders = (Array.isArray(log) ? log : [])
    .filter((entry) => /(odeslán|odeslan).*(po splatnosti|upomín)/i.test(String(entry?.text || '')))
    .sort((left, right) => new Date(String(right?.date || '').replace(' ', 'T')) - new Date(String(left?.date || '').replace(' ', 'T')));
  const latest = reminders[0];
  return latest ? { at: latest.date, text: latest.text } : null;
}

function ReminderLink({ invoice, onOpen }) {
  if (!invoice.lastReminder) return '-';
  return (
    <button type="button" className="crm-invoice-log-link" title={invoice.lastReminder.text} onClick={() => onOpen(invoice)}>
      {asDateTime(invoice.lastReminder.at)}
    </button>
  );
}

function normalizeInvoice(raw, customersByIco) {
  const ico = normalizeIco(firstValue(raw, ['customer_IC', 'customer_ico', 'ico', 'IC']));
  const customer = customersByIco.get(ico);
  const id = firstValue(raw, ['id']);
  const lastReminder = lastOverdueReminder(raw?.log);
  return {
    id,
    number: firstValue(raw, ['number']),
    supplierName: firstValue(raw, ['supplier_name']),
    subject: firstValue(raw, ['customer_name']),
    email: Array.isArray(raw?.mail_to) ? raw.mail_to.filter(Boolean).join(', ') : firstValue(raw, ['mail_to', 'customer_contact_mail']),
    lastReminder,
    log: Array.isArray(raw?.log) ? raw.log : [],
    dueDate: firstValue(raw, ['date_due']),
    issueDate: firstValue(raw, ['date_created']),
    amount: firstValue(raw, ['total']),
    currency: firstValue(raw, ['currency']) || 'CZK',
    url: id ? `https://app.vyfakturuj.cz/faktury/detail/${id}/` : firstValue(raw, ['url_app_detail']),
    customerId: Number(customer && customer.ZakaznikId),
    customerName: String((customer && customer.Nazev) || firstValue(raw, ['customer_name']) || ''),
    ico,
    knownInsolvency: typeof raw.crmInsolvence === 'boolean' ? raw.crmInsolvence : null,
    overdueDays: overdueDays(firstValue(raw, ['date_due'])),
  };
}

function insolvencyMessage(result) {
  if (!result) return '';
  if (result.error) return result.error;
  if (!result.inInsolvency) return 'Bez aktivního insolvenčního řízení.';
  return `V insolvenci: ${(result.cases || []).map((item) => item.status).filter(Boolean).join(', ') || 'aktivní řízení'}`;
}

function formatCzk(value) {
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(Number(value) || 0);
}

const NezaplaceneFaktury = () => {
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ageFilter, setAgeFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('all') === '1' ? 'all' : params.get('includeOld') === '1' ? 'old' : 'active';
  });
  const [insolvencies, setInsolvencies] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [selectedLogInvoice, setSelectedLogInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [result, customers] = await Promise.all([fetchUnpaidInvoices({ includeOld: true }), fetchCustomers()]);
      const customersByIco = new Map(
        customers
          .filter((customer) => normalizeIco(customer.IC))
          .map((customer) => [normalizeIco(customer.IC), customer])
      );
      const normalized = result.items.map((item) => normalizeInvoice(item, customersByIco));
      setInvoices(normalized);
      setInsolvencies(Object.fromEntries(
        normalized
          .filter((invoice) => invoice.ico && invoice.knownInsolvency !== null)
          .map((invoice) => [invoice.ico, { inInsolvency: invoice.knownInsolvency, cases: [], cached: true }])
      ));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Načtení nezaplacených faktur selhalo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const byAge = invoices.filter((invoice) => {
      if (ageFilter === 'recent') return invoice.overdueDays <= 30;
      if (ageFilter === 'old') return invoice.overdueDays > 365;
      if (ageFilter === 'all') return true;
      return invoice.overdueDays > 30 && invoice.overdueDays <= 365;
    });
    if (!query) return byAge;
    return byAge.filter((invoice) => [
      invoice.number,
      invoice.subject,
      invoice.customerName,
      invoice.ico,
      invoice.email,
    ].join(' ').toLowerCase().includes(query));
  }, [invoices, search, ageFilter]);

  const invoiceStats = useMemo(() => {
    const active = invoices.filter((invoice) => invoice.overdueDays > 30 && invoice.overdueDays <= 365);
    const old = invoices.filter((invoice) => invoice.overdueDays > 365);
    const insolvency = invoices.filter((invoice) => insolvencies[invoice.ico]?.inInsolvency === true);
    return {
      activeCount: active.length,
      activeAmount: active.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0),
      oldCount: old.length,
      oldAmount: old.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0),
      insolvencyCount: insolvency.length,
      insolvencyAmount: insolvency.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0),
    };
  }, [invoices, insolvencies]);

  const checkVisibleInsolvencies = async () => {
    const targets = [...new Map(filtered
      .filter((invoice) => invoice.ico)
      .map((invoice) => [invoice.ico, invoice])).values()].slice(0, 45);
    if (!targets.length) return;
    setInsolvencies((previous) => ({
      ...previous,
      ...Object.fromEntries(targets.map((invoice) => [invoice.ico, { loading: true }])),
    }));
    for (let index = 0; index < targets.length; index += 3) {
      const batch = targets.slice(index, index + 3);
      const results = await Promise.all(batch.map(async (invoice) => {
        try {
          return [invoice.ico, await checkInsolvencyByIco({ ico: invoice.ico })];
        } catch (e) {
          return [invoice.ico, { error: e instanceof Error ? e.message : 'Kontrola selhala.' }];
        }
      }));
      setInsolvencies((previous) => ({ ...previous, ...Object.fromEntries(results) }));
    }
  };

  return (
    <div>
      <h2 className="page-header">Nezaplacené faktury</h2>
      <div className="row dashboard-stats-row">
        <div className="dashboard-stats-col col-md-4 col-sm-12"><StatusCard icon="bx bx-receipt" count={loading ? '...' : formatCzk(invoiceStats.activeAmount)} title={`Po splatnosti 31–365 dní (${invoiceStats.activeCount})`} countClassName="status-card__count--compact" /></div>
        <div className="dashboard-stats-col col-md-4 col-sm-12"><StatusCard icon="bx bx-error-circle" count={loading ? '...' : formatCzk(invoiceStats.oldAmount)} title={`Nedobytné nad 1 rok (${invoiceStats.oldCount})`} countClassName="status-card__count--compact" /></div>
        <div className="dashboard-stats-col col-md-4 col-sm-12"><StatusCard icon="bx bx-shield-quarter" count={loading ? '...' : formatCzk(invoiceStats.insolvencyAmount)} title={`Zákazníci v insolvenci (${invoiceStats.insolvencyCount} faktur)`} countClassName="status-card__count--compact" /></div>
      </div>
      <div className="card">
        <div className="crm-toolbar crm-toolbar--invoices">
          <input
            placeholder="Hledat podle čísla, zákazníka nebo IČ..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select value={ageFilter} onChange={(event) => setAgeFilter(event.target.value)} aria-label="Stáří pohledávky">
            <option value="active">31–365 dní po splatnosti</option>
            <option value="recent">1–30 dní po splatnosti</option>
            <option value="old">Nedobytné: nad 1 rok</option>
            <option value="all">Vše po splatnosti</option>
          </select>
          <button type="button" className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Načítám...' : 'Obnovit'}
          </button>
          <button type="button" className="btn btn-primary" onClick={checkVisibleInsolvencies} disabled={loading || !filtered.length}>Ověřit insolvenci zobrazeného seznamu</button>
        </div>

        {error ? <p className="crm-error">{error}</p> : null}

        <Table
          headData={isMobile ? ['Faktura'] : ['Číslo', 'Naše firma', 'Zákazník', 'IČ', 'E-mail příjemce', 'Posl. upomínka', 'Splatnost', 'Po splatnosti', 'Částka', 'Insolvence']}
          renderHead={(item, index) => <th key={index}>{item}</th>}
          bodyData={filtered}
          renderBody={(invoice, index) => (
            <tr key={`${invoice.id || invoice.number || 'invoice'}-${index}`}>
              {isMobile ? (
                <td>
                  <div className="crm-mobile-stack">
                    <div className="crm-mobile-stack__header">
                      <div className="crm-mobile-stack__title-group">
                        {invoice.url ? <a className="crm-invoice-number-link" href={invoice.url} target="_blank" rel="noreferrer">{invoice.number || 'Faktura'}</a> : <span className="crm-mobile-stack__eyebrow">{invoice.number || 'Faktura'}</span>}
                        <span className="crm-mobile-stack__title">{invoice.customerName || invoice.subject || '-'}</span>
                      </div>
                      <strong>{asAmount(invoice.amount, invoice.currency)}</strong>
                    </div>
                    <span className="crm-mobile-stack__line">IČ: {invoice.ico || '-'}</span>
                    <span className="crm-mobile-stack__line">E-mail příjemce: {invoice.email || '-'}</span>
                    <span className="crm-mobile-stack__line">Poslední upomínka: <ReminderLink invoice={invoice} onOpen={setSelectedLogInvoice} /></span>
                    <span className="crm-mobile-stack__line">Naše firma: {invoice.supplierName || '-'}</span>
                    <span className="crm-mobile-stack__line">Splatnost: {asDate(invoice.dueDate)} · {invoice.overdueDays} dní po splatnosti</span>
                    {invoice.overdueDays > 365 ? <span className="crm-error">Nedobytná: více než rok po splatnosti</span> : null}
                    {insolvencies[invoice.ico] && !insolvencies[invoice.ico].loading ? (
                      <span className={insolvencies[invoice.ico].inInsolvency ? 'crm-error' : 'crm-form-note'}>{insolvencyMessage(insolvencies[invoice.ico])}</span>
                    ) : null}
                  </div>
                </td>
              ) : (
                <>
                  <td>{invoice.url ? <a className="crm-invoice-number-link" href={invoice.url} target="_blank" rel="noreferrer">{invoice.number || '-'}</a> : invoice.number || '-'}</td>
                  <td>{invoice.supplierName || '-'}</td>
                  <td>{invoice.customerName || invoice.subject || '-'}</td>
                  <td>{invoice.ico || '-'}</td>
                  <td>{invoice.email || '-'}</td>
                  <td><ReminderLink invoice={invoice} onOpen={setSelectedLogInvoice} /></td>
                  <td>{asDate(invoice.dueDate)}</td>
                  <td>{invoice.overdueDays} dní{invoice.overdueDays > 365 ? ' (nedobytná)' : ''}</td>
                  <td>{asAmount(invoice.amount, invoice.currency)}</td>
                  <td className={insolvencies[invoice.ico] && insolvencies[invoice.ico].inInsolvency ? 'crm-error' : ''}>
                    {insolvencies[invoice.ico] ? (insolvencies[invoice.ico].loading ? 'Ověřuji…' : insolvencyMessage(insolvencies[invoice.ico])) : '-'}
                  </td>
                </>
              )}
            </tr>
          )}
        />
        {!loading && !error && filtered.length === 0 ? <p style={{ marginTop: 15 }}>Žádné nezaplacené faktury nenalezeny.</p> : null}
      </div>
      {selectedLogInvoice ? (
        <div className="crm-modal-backdrop" role="dialog" aria-modal="true" aria-label={`Historie faktury ${selectedLogInvoice.number || ''}`}>
          <div className="crm-modal-card crm-invoice-log-modal">
            <div className="crm-modal-header">
              <h3>Historie faktury {selectedLogInvoice.number || ''}</h3>
              <button type="button" className="crm-modal-close-btn" aria-label="Zavřít historii" onClick={() => setSelectedLogInvoice(null)}><i className="bx bx-x" /></button>
            </div>
            <div className="crm-invoice-log-list">
              {[...selectedLogInvoice.log]
                .sort((left, right) => new Date(String(right?.date || '').replace(' ', 'T')) - new Date(String(left?.date || '').replace(' ', 'T')))
                .map((entry, index) => (
                  <div className="crm-invoice-log-list__item" key={`${entry?.date || 'log'}-${index}`}>
                    <time>{asDateTime(entry?.date)}</time>
                    <span>{entry?.text || '-'}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NezaplaceneFaktury;
